import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS, ROUTES } from "../../../Utils/constants";
import WestIcon from "@mui/icons-material/West";
import {
  Box,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  InputBase,
  Divider,
  FormGroup,
  Checkbox,
  FormControlLabel,
  Switch,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ConfirmationModal from "../../../Components/Common/ConfirmationModal";
import { useLocation, useNavigate } from "react-router-dom";
import {
  addVendingMachine,
  updateVendingMachine,
} from "../../../Services/authService";
import SuccessNotification from "../../../Components/Common/SuccessNotification";
import { formatDate } from "../../../Components/Common/dateFormat";
import ScreenInformationSection from "./ScreenInformation";
import machineSpecifications from "./machineSpecifications";
import LoadingAnimation from "../../../Components/Common/LoadingAnimation";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CalendarToday } from "@mui/icons-material";
import {
  DropDownIcon,
  BlueTickIcon,
} from "../../../Components/Layouts/CustomIcons";

function VendingMachineModal() {
  const location = useLocation();
  const { companyId } = location.state || {};
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { mode } = useThemeContext();
  const { state } = location;
  const mode1 = state?.mode || "add"; // Determine mode: 'add' or 'update'
  // console.log("[VendingMachineModal] location.state:", state);
  const machineData = state?.machineData?.vendingMachine || {}; // Existing machine data for update
  // Also try reading machineType from the flat response in case the vendingMachine wrapper is absent
  const machineTypeVal =
    machineData?.machineType ||
    state?.machineData?.machineType ||
    state?.machineData?.type ||
    state?.machineData?.vendingMachine?.type ||
    "";
  // console.log(
  //   "[VendingMachineModal] Resolved machineType:",
  //   JSON.stringify(machineTypeVal),
  //   "| machineData.machineType:",
  //   JSON.stringify(machineData?.machineType),
  // );

  // Recycle/return machines were renamed from "Return Vending Machine" to
  // "Greeny Recycle Machine" — match both old and new names like other views
  const isReturnMachine = (type) => {
    const typeName = (type || "").toLowerCase();
    return (
      typeName.includes("return") ||
      typeName.includes("recycle") ||
      typeName.includes("greeny")
    );
  };

  // "Bowls Not Supported" toggle applies only to the with-heating (Hot) and
  // without-heating (Normal) vending machine types
  const supportsBowlsToggle = (type) =>
    type === "Hot Vending Machine" || type === "Normal Vending Machine";

  // Format ISO date string using the standard dateFormat utility
  // ponytail: one-line wrapper avoids repeating the ternary in every formatDate call
  const fmtDate = (v) => (v ? formatDate(v) : "");
const DEFAULT_BOWL_CASHBACK = 2;
const DEFAULT_BOTTLE_CASHBACK = 0.25;

  const initialFormState = {
    companyId: machineData?.companyId || 1,
    machineNo: machineData?.machineNumber || "",
    machineType: machineTypeVal,
    returnItemType: Array.isArray(machineData?.returnItemType)
      ? machineData.returnItemType
      : machineData?.returnItemType
        ? [machineData.returnItemType]
        : [],
    location: machineData?.location || "",
    streetAddress: machineData?.street || "",
    city: machineData?.city || "",
    state: machineData?.state || "",
    zip: machineData?.zip || "",
    latitude: machineData?.latitude ?? "",
    longitude: machineData?.longitude ?? "",
    heatingPrice: machineData?.heatingPrice || "",
    bowlsNotSupported: Boolean(
      machineData?.bowlsNotSupported ??
        state?.machineData?.bowlsNotSupported ??
        false,
    ),
    machineConfig: {
      length: machineData?.machineConfig?.length || "",
      height: machineData?.machineConfig?.height || "",
      width: machineData?.machineConfig?.width || "",
      slots: machineData?.machineConfig?.slots || "",
      rows: machineData?.machineConfig?.totalRows || "",
      columns: machineData?.machineConfig?.totalColumns || "",
      weight: machineData?.machineConfig?.weight || "",
      bowlCapacity: machineData?.machineConfig?.bowlCapacity || 0,
      bottleCapacity: machineData?.machineConfig?.bottleCapacity || 0,
     bowlCashback:
  machineData?.machineConfig?.bowlCashback ??
  machineData?.bowlCashback ??
  (isReturnMachine(machineTypeVal) ? DEFAULT_BOWL_CASHBACK : ""),

bottleCashback:
  machineData?.machineConfig?.bottleCashback ??
  machineData?.bottleCashback ??
  (isReturnMachine(machineTypeVal) ? DEFAULT_BOTTLE_CASHBACK : ""),
    },
    temperatureHumidity: {
      minTemperature: machineData?.temperatureHumidity?.minTemperature || "",
      maxTemperature: machineData?.temperatureHumidity?.maxTemperature || "",
      defrostInterval: machineData?.temperatureHumidity?.defrostInterval || "",
      defrostDuration: machineData?.temperatureHumidity?.defrostDuration || "",
    },
    connectivityAndPower: {
      // Backend returns these flat on the machine — fall back to nested for
      // API variants that nest them
      networkConnectionType:
        machineData?.networkConnectionType ||
        machineData?.connectivityAndPower?.networkConnectionType ||
        "",
      powerSource: machineData?.connectivityAndPower?.powerSource || "",
      macAddress: machineData?.connectivityAndPower?.macAddress || "",
      powerConsumption:
        machineData?.powerConsumption ||
        machineData?.connectivityAndPower?.powerConsumption ||
        "",
      activationDate: (() => {
        const raw =
          machineData?.activationDate ||
          machineData?.connectivityAndPower?.activationDate;
        return raw ? new Date(raw).toISOString().split("T")[0] : "";
      })(),
      warrantyPeriod:
        machineData?.warrantyPeriod ||
        machineData?.connectivityAndPower?.warrantyPeriod ||
        "",
    },
    androidAppInfo: {
      version:
        machineData?.version || machineData?.androidAppInfo?.version || "",
      // lastUpdated is a dropdown (relative string), not a date — read directly
      lastUpdated:
        machineData?.lastUpdated ||
        machineData?.androidAppInfo?.lastUpdated ||
        "",
      lastUpdatedVersion: fmtDate(
        machineData?.lastUpdatedVersion ||
          machineData?.androidAppInfo?.lastUpdatedVersion ||
          "",
      ),
      versionNotes:
        machineData?.versionNotes ||
        machineData?.androidAppInfo?.versionNotes ||
        machineData?.androidAppInfo?.notes ||
        "",
      // Convert numeric codes to strings for dropdown value matching
      paymentType: String(
        machineData?.paymentType ??
          machineData?.androidAppInfo?.paymentType ??
          "",
      ),
      aisleType: String(
        machineData?.aisleType ?? machineData?.androidAppInfo?.aisleType ?? "",
      ),
    },
    screens: machineData?.screens || [
      {
        width: "40",
        height: "80",
        resolution: "1080x1920 ",
        displayType: "LCD",
      },
    ],
  };

  const [formData, setFormData] = useState(initialFormState);
  const [imagePreview, setImagePreview] = useState(
    state?.machineData?.imageUrl || null,
  );
  const [imageFile, setImageFile] = useState(null); // File object for the image
  const [errors, setErrors] = useState({}); // Validation errors
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false); // Tracks if the form is modified
  const [isLoading, setIsLoading] = useState(false);
  const [successNotification, setSuccessNotification] = useState(false);
  const dateInputRef = useRef(null);

  const backgroundCard =
    mode === "dark" ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const btnColor =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const cardBorderColor =
    mode === "dark" ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale30;
  const inputBoxColor =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const textColor =
    mode === "dark"
      ? DARKCOLORS.HeadingLightBackground
      : LIGHTCOLORS.HeadingLightBackground;
  const errorTextColor =
    mode === "dark" ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;
  const subHeadingGrey =
    mode === "dark"
      ? DARKCOLORS.DisabledLightBackground
      : LIGHTCOLORS.DisabledLightBackground;
  const dropCircle =
    mode === "dark" ? DARKCOLORS.GreyScale50 : LIGHTCOLORS.GreyScale50;
  const dropIcon =
    mode === "dark" ? DARKCOLORS.GreyScale80 : LIGHTCOLORS.GreyScale80;

  const dropDownIconComponent = useCallback(
    (props) => (
      <DropDownIcon color1={dropCircle} color2={dropIcon} {...props} />
    ),
    [dropCircle, dropIcon],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // const normalizeScreens = (screens) => {
  //     // Remove empty objects from screens array
  //     return screens.filter(screen =>
  //         Object.values(screen).some(value => value !== "")
  //     );
  // };

  const deepEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true;

    if (
      typeof obj1 !== "object" ||
      typeof obj2 !== "object" ||
      obj1 === null ||
      obj2 === null
    ) {
      return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key)) return false;

      if (!deepEqual(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  };

  // const fetchLatLng = async (loc, streetAddr, cityParam) => {
  //     // Print incoming pieces for easier debugging
  //     const addressParts = [loc, streetAddr, cityParam].filter(part => part && part.trim().length > 0);
  //     console.log('Geocode input parts:', { location: loc, streetAddress: streetAddr, city: cityParam, addressParts });

  //     if (addressParts.length === 0 || addressParts.join('').trim().length < 3) {
  //         console.log('Geocode skipped: insufficient address parts');
  //         return; // Ignore if no valid address
  //     }

  //     const fullAddress = addressParts.join(', ');

  //     const performSearch = async (query) => {
  //         const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=1`;
  //         try {
  //             const response = await fetch(url);
  //             if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  //             const data = await response.json();
  //             return data;
  //         } catch (err) {
  //             console.error('Geocode fetch error for query:', query, err);
  //             return null;
  //         }
  //     };

  //     // Try the most specific query first, then fall back to simpler queries
  //     const queries = [fullAddress, `${streetAddr}, ${cityParam}`, streetAddr, loc, cityParam];
  //     let found = null;
  //     const tried = new Set();

  //     for (const q of queries) {
  //         if (!q) continue;
  //         const trimmed = q.trim();
  //         if (trimmed.length < 3 || tried.has(trimmed)) continue;
  //         tried.add(trimmed);
  //         console.log('Geocoding query:', trimmed);
  //         const data = await performSearch(trimmed);
  //         if (data && data.length > 0) {
  //             console.log('Geocode success for query:', trimmed, data[0]);
  //             found = data[0];
  //             break;
  //         } else {
  //             console.log('No geocode result for query:', trimmed);
  //         }
  //     }

  //     if (found) {
  //         setFormData((prevData) => ({
  //             ...prevData,
  //             latitude: parseFloat(found.lat),
  //             longitude: parseFloat(found.lon),
  //         }));
  //         console.log('Updated formData lat/lng:', { lat: parseFloat(found.lat), lon: parseFloat(found.lon) });
  //     } else {
  //         console.log('Geocode: no results found for any query');
  //         setFormData((prevData) => ({ ...prevData, latitude: "", longitude: "" }));
  //     }
  // };

  // // ✅ Optimized: Fetch data with debounce (1s delay)
  // const updateLatLng = useCallback(() => {
  //     const timeoutId = setTimeout(() => fetchLatLng(formData.location, formData.streetAddress, formData.city), 1000);
  //     return () => clearTimeout(timeoutId);
  // }, [formData.location, formData.streetAddress, formData.city]);

  // useEffect(updateLatLng, [updateLatLng]);

  // Pre-populate fields when a machine type with predefined specs is selected

  useEffect(() => {
    // ponytail: in update mode the API data is already loaded into
    // initialFormState — don't overwrite it with empty spec defaults
    if (mode1 === "update") return;

    setFormData((prev) => {
      const spec = machineSpecifications[formData.machineType];

       const isReturn = isReturnMachine(formData.machineType);
  const keepOrDefault = (current, fallback) =>
    current === "" || current == null ? fallback : current;

      // Start from clean defaults so fields from a previous spec get cleared
      const baseMachineConfig = {
        length: "",
        height: "",
        width: "",
        slots: "",
        rows: "",
        columns: "",
        weight: "",
        bowlCapacity: 0,
        bottleCapacity: 0,
        bowlCashback: isReturn
      ? keepOrDefault(prev.machineConfig.bowlCashback, DEFAULT_BOWL_CASHBACK)
      : "",
    bottleCashback: isReturn
      ? keepOrDefault(prev.machineConfig.bottleCashback, DEFAULT_BOTTLE_CASHBACK)
      : "",
      };
      
      const baseConnectivity = {
        networkConnectionType: "",
        powerSource: "",
        macAddress: "",
        powerConsumption: "",
        activationDate: "",
        warrantyPeriod: "",
      };
      const baseScreens = [
        {
          // width: "40",
          height: "80",
          resolution: "1080x1920",
          displayType: "LCD",
        },
      ];

      if (!spec) {
        // No spec for this type — reset to empty defaults
        return {
          ...prev,
          machineConfig: baseMachineConfig,
          connectivityAndPower: baseConnectivity,
          screens: baseScreens,
        };
      }

      // Overlay spec values on top of defaults
      const newMachineConfig = { ...baseMachineConfig };

      Object.entries(spec.machineConfig || {}).forEach(([key, value]) => {
        // Keep user-entered cashback values
        if (key === "bowlCashback" || key === "bottleCashback") return;

        if (value !== undefined && value !== null) {
          newMachineConfig[key] = value;
        }
      });

      const newConnectivity = { ...baseConnectivity };
      Object.entries(spec.connectivityAndPower || {}).forEach(
        ([key, value]) => {
          if (value !== undefined && value !== null) {
            newConnectivity[key] = value;
          }
        },
      );

      return {
        ...prev,
        machineConfig: newMachineConfig,
        connectivityAndPower: newConnectivity,
        screens: spec.screens || baseScreens,
      };
    });
  }, [formData.machineType, mode1]);

  // Watch for changes to form data to track dirty state
  useEffect(() => {
    // console.log(initialFormState, formData);

    if (mode1 === "add")
      setIsDirty(JSON.stringify(initialFormState) !== JSON.stringify(formData));
    else setIsDirty(!deepEqual(initialFormState, formData));
  }, [formData, initialFormState, mode1]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    // Nested Field Handling for Machine Configuration
    if (formData.machineConfig && name in formData.machineConfig) {
      setFormData({
        ...formData,
        machineConfig: {
          ...formData.machineConfig,
          [name]: value,
        },
      });
    } else if (
      formData.connectivityAndPower &&
      name in formData.connectivityAndPower
    ) {
      setFormData({
        ...formData,
        connectivityAndPower: {
          ...formData.connectivityAndPower,
          [name]: value,
        },
      });
    } else if (formData.androidAppInfo && name in formData.androidAppInfo) {
      setFormData({
        ...formData,
        androidAppInfo: {
          ...formData.androidAppInfo,
          [name]: value,
        },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    setErrors({ ...errors, [name]: "" }); // Clear errors on input change
  };

  const handleReturnItemToggle = (item) => () => {
    const current = formData.returnItemType;
    const updated = current.includes(item)
      ? current.filter((i) => i !== item)
      : [...current, item];
    setFormData({ ...formData, returnItemType: updated });
    setErrors({ ...errors, returnItemType: "" });
  };

  // const validate = () => {
  //     let tempErrors = {};

  //     if (!formData.machineNo) tempErrors.machineNo = 'Machine No. is required';
  //     if (!formData.machineType) tempErrors.machineType = 'Machine Type is required';
  //     if (!formData.location) tempErrors.location = 'Location is required';
  //     if (!formData.streetAddress) tempErrors.streetAddress = 'Street Address is required';
  //     if (!formData.city) tempErrors.city = 'City is required';
  //     if (!formData.state) tempErrors.state = 'State is required';
  //     if (!formData.zip) tempErrors.zip = 'Zip code is required';
  //     else if (!/^\d{5,6}$/.test(formData.zip)) tempErrors.zip = 'Zip code must be 5-6 digits';

  //     // Machine Configuration Validation (Optional Fields)
  //     const { machineConfig } = formData;

  //     if (!machineConfig?.length) {
  //         tempErrors.length = 'Length is required';
  //     } else if (isNaN(machineConfig.length)) {
  //         tempErrors.length = 'Length must be a number';
  //     }
  //     if (!machineConfig?.height) {
  //         tempErrors.height = 'Height is required';
  //     } else if (isNaN(machineConfig.height)) {
  //         tempErrors.height = 'Height must be a number';
  //     }
  //     if (!machineConfig?.width) {
  //         tempErrors.width = 'Width is required';
  //     } else if (isNaN(machineConfig.width)) {
  //         tempErrors.width = 'Width must be a number';
  //     }
  //     if (!machineConfig?.rows) {
  //         tempErrors.rows = 'Rows are required';
  //     } else if (!Number.isInteger(Number(machineConfig.rows)) || isNaN(machineConfig.rows)) {
  //         tempErrors.rows = 'Rows must be an integer';
  //     }
  //     if (!machineConfig?.columns) {
  //         tempErrors.columns = 'Columns are required';
  //     } else if (!Number.isInteger(Number(machineConfig.columns)) || isNaN(machineConfig.columns)) {
  //         tempErrors.columns = 'Columns must be an integer';
  //     }
  //     if (!machineConfig?.weight) {
  //         tempErrors.weight = 'Weight is required';
  //     } else if (isNaN(machineConfig.weight)) {
  //         tempErrors.weight = 'Weight must be a number';
  //     }

  //     if (!formData.connectivityAndPower?.networkConnectionType) {
  //         tempErrors.networkConnectionType = 'Network Connection Type is required';
  //     }

  //     if (!formData.connectivityAndPower?.powerSource) {
  //         tempErrors.powerSource = 'Power Source is required';
  //     }

  //     if (!formData.connectivityAndPower?.powerConsumption) {
  //         tempErrors.powerConsumption = 'Power Consumption is required';
  //     } else if (isNaN(formData.connectivityAndPower.powerConsumption)) {
  //         tempErrors.powerConsumption = 'Power Consumption must be a number';
  //     }
  //     // if (formData.connectivityAndPower.macAddress && !/^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(formData.connectivityAndPower.macAddress)) {
  //     //     tempErrors.macAddress = 'Invalid MAC Address format';
  //     // }
  //     if (formData.connectivityAndPower.activationDate && isNaN(new Date(formData.connectivityAndPower.activationDate).getTime())) {
  //         tempErrors.activationDate = 'Activated Date must be a valid date';
  //     }
  //     if (formData.connectivityAndPower.warrantyPeriod && (!Number.isInteger(Number(formData.connectivityAndPower.warrantyPeriod)) || isNaN(formData.connectivityAndPower.warrantyPeriod))) {
  //         tempErrors.warrantyPeriod = 'Warranty Period must be a valid number';
  //     }

  //     setErrors(tempErrors);
  //     return Object.keys(tempErrors).length === 0;
  // };

  const validate = () => {
    let tempErrors = {};
    let firstError = "";

    if (!formData.machineNo) {
      tempErrors.machineNo = t("vendingMachines.Machineno");
      firstError ||= tempErrors.machineNo;
    }
    if (!formData.machineType) {
      tempErrors.machineType = t("vendingMachines.MachineTyperequired");
      firstError ||= tempErrors.machineType;
    }
    if (!formData.location) {
      tempErrors.location = t("vendingMachines.Locationis");
      firstError ||= tempErrors.location;
    }
    if (!formData.streetAddress) {
      tempErrors.streetAddress = t("vendingMachines.StreetAddress");
      firstError ||= tempErrors.streetAddress;
    }
    if (!formData.city) {
      tempErrors.city = t("vendingMachines.Cityis");
      firstError ||= tempErrors.city;
    }

    if (formData.latitude === "" || formData.latitude == null) {
      tempErrors.latitude = t("vendingMachines.latRequired");
      firstError ||= tempErrors.latitude;
    } else if (formData.latitude < -90 || formData.latitude > 90) {
      tempErrors.latitude = t("vendingMachines.latInvalid");
      firstError ||= tempErrors.latitude;
    }

    if (formData.longitude === "" || formData.longitude == null) {
      tempErrors.longitude = t("vendingMachines.longRequired");
      firstError ||= tempErrors.longitude;
    } else if (formData.longitude < -180 || formData.longitude > 180) {
      tempErrors.longitude = t("vendingMachines.longInvalid");
      firstError ||= tempErrors.longitude;
    }

    if (!formData.state) {
      tempErrors.state = t("vendingMachines.Stateis");
      firstError ||= tempErrors.state;
    }
    if (!formData.zip) {
      tempErrors.zip = t("vendingMachines.Zipcode");
      firstError ||= tempErrors.zip;
    } else if (!/^\d{5,6}$/.test(formData.zip)) {
      tempErrors.zip = t("vendingMachines.Zipcodemust");
      firstError ||= tempErrors.zip;
    }

    const { machineConfig } = formData;

    // if (!machineConfig?.length) {
    //     tempErrors.length = t('vendingMachines.Lengthrequired');
    //     firstError ||= tempErrors.length;
    // } else if (isNaN(machineConfig.length)) {
    //     tempErrors.length = t('vendingMachines.Lengthmust');
    //     firstError ||= tempErrors.length;
    // }

    // if (!machineConfig?.height) {
    //     tempErrors.height = t('vendingMachines.Heightis');
    //     firstError ||= tempErrors.height;
    // } else if (isNaN(machineConfig.height)) {
    //     tempErrors.height = t('vendingMachines.Heightmust');
    //     firstError ||= tempErrors.height;
    // }

    // if (!machineConfig?.width) {
    //     tempErrors.width = t('vendingMachines.Widthis');
    //     firstError ||= tempErrors.width;
    // } else if (isNaN(machineConfig.width)) {
    //     tempErrors.width = t('vendingMachines.Widthmust');
    //     firstError ||= tempErrors.width;
    // }

    if (!isReturnMachine(formData.machineType)) {
      if (!machineConfig?.rows) {
        tempErrors.rows = t("vendingMachines.Rowsare");
        firstError ||= tempErrors.rows;
      } else if (
        !Number.isInteger(Number(machineConfig.rows)) ||
        isNaN(machineConfig.rows)
      ) {
        tempErrors.rows = t("vendingMachines.Rowsmust");
        firstError ||= tempErrors.rows;
      }

      if (!machineConfig?.columns) {
        tempErrors.columns = t("vendingMachines.Columnsare");
        firstError ||= tempErrors.columns;
      } else if (
        !Number.isInteger(Number(machineConfig.columns)) ||
        isNaN(machineConfig.columns)
      ) {
        tempErrors.columns = t("vendingMachines.Columnsmust");
        firstError ||= tempErrors.columns;
      }
    }

    // if (!machineConfig?.weight) {
    //     tempErrors.weight = t('vendingMachines.Weightis');
    //     firstError ||= tempErrors.weight;
    // } else if (isNaN(machineConfig.weight)) {
    //     tempErrors.weight = t('vendingMachines.Weightmust');
    //     firstError ||= tempErrors.weight;
    // }

    if (!formData.connectivityAndPower?.networkConnectionType) {
      tempErrors.networkConnectionType = t(
        "vendingMachines.NetworkConnectionrequired",
      );
      firstError ||= tempErrors.networkConnectionType;
    }

    // if (!formData.connectivityAndPower?.powerSource) {
    //     tempErrors.powerSource = t('vendingMachines.PowerSourcerequired');
    //     firstError ||= tempErrors.powerSource;
    // }

    // if (!formData.connectivityAndPower?.powerConsumption) {
    //     tempErrors.powerConsumption = t('vendingMachines.PowerConsumptionrequired');
    //     firstError ||= tempErrors.powerConsumption;
    // } else if (isNaN(formData.connectivityAndPower.powerConsumption)) {
    //     tempErrors.powerConsumption = t('vendingMachines.PowerConsumptionmust');
    //     firstError ||= tempErrors.powerConsumption;
    // }

    if (
      formData.connectivityAndPower.activationDate &&
      isNaN(new Date(formData.connectivityAndPower.activationDate).getTime())
    ) {
      tempErrors.activationDate = t("vendingMachines.ActivatedDaterequired");
      firstError ||= tempErrors.activationDate;
    }

    if (
      formData.connectivityAndPower.warrantyPeriod &&
      (!Number.isInteger(
        Number(formData.connectivityAndPower.warrantyPeriod),
      ) ||
        isNaN(formData.connectivityAndPower.warrantyPeriod))
    ) {
      tempErrors.warrantyPeriod = t("vendingMachines.WarrantyPeriodmust");
      firstError ||= tempErrors.warrantyPeriod;
    }

    // Bowl/Bottle capacity validation removed — values no longer required
    // ponytail: skip returnItemType validation in update mode — checkboxes are disabled, no way to fix
    if (isReturnMachine(formData.machineType)) {
      if (
        machineConfig?.bowlCashback === "" ||
        machineConfig?.bowlCashback == null
      ) {
        tempErrors.bowlCashback = t("vendingMachines.bowlCashbackRequired");
        firstError ||= tempErrors.bowlCashback;
      } else if (
        isNaN(machineConfig.bowlCashback) ||
        Number(machineConfig.bowlCashback) < 0 ||
        Number(machineConfig.bowlCashback) > 100
      ) {
        tempErrors.bowlCashback = t("vendingMachines.bowlCashbackInvalid");
        firstError ||= tempErrors.bowlCashback;
      }
      if (
        machineConfig?.bottleCashback === "" ||
        machineConfig?.bottleCashback == null
      ) {
        tempErrors.bottleCashback = t("vendingMachines.bottleCashbackRequired");
        firstError ||= tempErrors.bottleCashback;
      } else if (
        isNaN(machineConfig.bottleCashback) ||
        Number(machineConfig.bottleCashback) < 0 ||
        Number(machineConfig.bottleCashback) > 100
      ) {
        tempErrors.bottleCashback = t("vendingMachines.bottleCashbackInvalid");
        firstError ||= tempErrors.bottleCashback;
      }
    }
    // One top-level message when any required field is missing; API errors fall back
    const apiError =
      Object.keys(tempErrors).length > 0
        ? t("navbar.fillAllRequiredFields")
        : firstError;
    setErrors({ ...tempErrors, apiError });
    return Object.keys(tempErrors).length === 0;
  };

  const handleBackClick = () => {
    if (isDirty) {
      setIsModalOpen(true); // Open the modal only if the form is modified
    } else {
      if (machineData?.id)
        if (machineData?.companyId !== 1)
          navigate(`${ROUTES.MACHINE_DETAILS}/${machineData.id}`, {
            state: { franchise: "franchise" },
          });
        else navigate(`${ROUTES.MACHINE_DETAILS}/${machineData.id}`);
      else navigate(ROUTES.VENDING_MACHINES);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false); // Close the modal
  };

  const handleSaveChanges = () => {
    setIsModalOpen(false); // Close the modal after saving
    handleSubmit();
  };

  const handleDiscardChanges = () => {
    setIsModalOpen(false); // Close the modal after discarding
    if (machineData.id) navigate(`${ROUTES.MACHINE_DETAILS}/${machineData.id}`);
    else navigate(ROUTES.VENDING_MACHINES);
  };

  // const validateScreens = () => {
  //     let isValid = true;
  //     let screenErrors = [];
  //     let firstScreenError = '';

  //     formData.screens.forEach((screen, index) => {
  //         const screenError = {};
  //         if (!screen.width || isNaN(screen.width) || screen.width <= 0) {
  //             screenError.width = t('vendingMachines.Widthmust');
  //             firstScreenError ||= screenError.width;
  //             isValid = false;
  //         }
  //         if (!screen.height || isNaN(screen.height) || screen.height <= 0) {
  //             screenError.height = t('vendingMachines.Heightmust');
  //             firstScreenError ||= screenError.height;
  //             isValid = false;
  //         }
  //         if (!screen.resolution || !/^\d+x\d+$/.test(screen.resolution)) {
  //             screenError.resolution = t('vendingMachines.Resolutionmust');
  //             firstScreenError ||= screenError.resolution;
  //             isValid = false;
  //         }
  //         // if (!screen.displayType) {
  //         //     screenError.displayType = t('vendingMachines.DisplayTyperequired');
  //         //     firstScreenError ||= screenError.displayType;
  //         //     isValid = false;
  //         // }
  //         screenErrors[index] = screenError;
  //     });

  //     setErrors(prev => ({
  //         ...prev,
  //         screens: screenErrors,
  //         apiError: !isValid ? firstScreenError : ''
  //     }));

  //     return isValid;
  // };

  const handleSubmit = async () => {
    // console.log(
    //   "1. BEFORE SUBMIT - CASHBACK:",
    //   formData.machineConfig.bowlCashback,
    //   formData.machineConfig.bottleCashback,
    // );
    if (validate()) {
      try {
        if (mode1 === "update") {
          if (!isDirty && !imageFile) {
            setErrors({ apiError: t("vendingMachines.Pleaseupdate") });
            return;
          }
        }
        setIsLoading(true);
        const machineConfig = {
          length: formData.machineConfig.length || null,
          height: formData.machineConfig.height || null,
          width: formData.machineConfig.width || null,
          // slots: formData.machineConfig.slots || null,
          rows: Number(formData.machineConfig.rows ?? 0),
          columns: Number(formData.machineConfig.columns ?? 0),
          weight: formData.machineConfig.weight || null,
          bowlCapacity: 0,
          bottleCapacity: 0,
          bowlCashback:
            formData.machineConfig.bowlCashback !== ""
              ? Number(formData.machineConfig.bowlCashback)
              : null,

          bottleCashback:
            formData.machineConfig.bottleCashback !== ""
              ? Number(formData.machineConfig.bottleCashback)
              : null,
        };

        // Recursive function to clean empty fields, arrays, and objects
        const removeEmptyValues = (obj) => {
          return Object.entries(obj).reduce((acc, [key, value]) => {
            if (value && typeof value === "object" && !Array.isArray(value)) {
              const nestedObject = removeEmptyValues(value);
              if (Object.keys(nestedObject).length > 0) {
                acc[key] = nestedObject; // Add non-empty nested objects
              }
            } else if (value && Array.isArray(value)) {
              const filteredArray = value
                .map((item) =>
                  typeof item === "object" ? removeEmptyValues(item) : item,
                )
                .filter(
                  (item) => item !== undefined && item !== null && item !== "",
                );
              if (filteredArray.length > 0) {
                acc[key] = filteredArray; // Add non-empty arrays
              }
            } else if (
              value !== "" &&
              value !== undefined &&
              value !== null &&
              !Number.isNaN(value)
            ) {
              acc[key] = value; // Add valid scalar values
            }
            return acc;
          }, {});
        };

        // const modifiedScreens = formData.screens.map(screen => {
        //     const { id, ...rest } = screen; // Destructure to remove 'id'
        //     return rest; // Return the screen object without the 'id' field
        // });

        const cleanedData = removeEmptyValues({
          companyId: companyId || 1,
          machineNumber: formData.machineNo,
          machineType: formData.machineType,
          returnItemType: formData.returnItemType,
          // Always send an explicit true/false; only the Hot/Normal types can
          // set it to true
          bowlsNotSupported: supportsBowlsToggle(formData.machineType)
            ? Boolean(formData.bowlsNotSupported)
            : false,
          location: formData.location,
          street: formData.streetAddress,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          latitude: parseFloat(formData.latitude),
          longitude: parseFloat(formData.longitude),
          // machineConfig: formData.machineConfig, // This will be removed if all fields are empty
          machineConfig: Object.values(machineConfig).some(
            (value) => value !== null,
          )
            ? machineConfig
            : {},

          connectivityAndPower: formData.connectivityAndPower, // This will also be removed if empty
          // The backend stores these app-info/connectivity fields at the TOP
          // level of the machine (the GET response returns paymentType/
          // versionNotes/aisleType/version/powerConsumption flat) — send
          // them there as numeric codes; the nested copy is kept for APIs
          // that nest them
          version: formData.androidAppInfo.version || null,
          paymentType:
            formData.androidAppInfo.paymentType === ""
              ? null
              : Number(formData.androidAppInfo.paymentType),
          versionNotes: formData.androidAppInfo.versionNotes || null,
          aisleType:
            formData.androidAppInfo.aisleType === ""
              ? null
              : Number(formData.androidAppInfo.aisleType),
          networkConnectionType:
            formData.connectivityAndPower.networkConnectionType || null,
          powerConsumption:
            formData.connectivityAndPower.powerConsumption === ""
              ? null
              : Number(formData.connectivityAndPower.powerConsumption),
          activationDate: formData.connectivityAndPower.activationDate
            ? new Date(
                formData.connectivityAndPower.activationDate,
              ).toISOString()
            : null,
          warrantyPeriod:
            formData.connectivityAndPower.warrantyPeriod === ""
              ? null
              : Number(formData.connectivityAndPower.warrantyPeriod),
          // lastUpdatedVersion is display-only (formatted date) — never send
          // it back, the formatted string would corrupt the stored value
          androidAppInfo: {
            ...formData.androidAppInfo,
            lastUpdatedVersion: undefined,
          },
          screens: formData.screens, // Empty screens will be removed
          temperatureHumidity: {
            minTemperature: 2,
            maxTemperature: 8,
            liveTemperature: 0,
            defrostInterval: 6,
            defrostDuration: 15,
          }, // heatingPrice: 2.5,
        });

        // console.log("[VendingMachineModal] submit cleanedData:", cleanedData);
        // console.log(
        //   "2. CLEANED DATA - CASHBACK:",
        //   cleanedData.machineConfig?.bowlCashback,
        //   cleanedData.machineConfig?.bottleCashback,
        // );

        // Create FormData object
        const formDataToSend = new FormData();
        formDataToSend.append("json", JSON.stringify(cleanedData));
        if (imageFile) formDataToSend.append("file", imageFile);

        const apiPayload = JSON.parse(formDataToSend.get("json"));

        // console.log(
        //   "3. API PAYLOAD - CASHBACK:",
        //   apiPayload.machineConfig?.bowlCashback,
        //   apiPayload.machineConfig?.bottleCashback,
        // );

        // Conditional API call
        if (mode1 === "add") {
          // POST API call for adding a new vending machine
          await addVendingMachine(formDataToSend);
        } else if (mode1 === "update") {
          // PATCH API call for updating an existing vending machine
          const machineId = machineData.id; // Ensure you have the machine ID for update
          await updateVendingMachine(machineId, formDataToSend); // Update API function
        }

        setIsLoading(false);
        setSuccessNotification(true); // Show success notification
        // Add delay to show the notification before navigating
        setTimeout(() => {
          navigate(ROUTES.VENDING_MACHINES);
        }, 3000); // 3-second delay
      } catch (error) {
        setIsLoading(false);

        const backendMessage = error?.response?.data?.detail || "";

        if (backendMessage.includes("already exists")) {
          setErrors({
            apiError: t("vendingMachines.machineAlreadyExists"),
            // or "A vending machine with this machine number already exists."
          });
        } else {
          setErrors({
            apiError: backendMessage || t("vendingMachines.Anunexpected"),
          });
        }

        console.error(t("vendingMachines.errorAddingVendingMachine"), error);
      }
    }
  };

  // Handle Image Selection
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validImageTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/gif",
      ];
      if (!validImageTypes.includes(file.type)) {
        setErrors({ ...errors, image: t("vendingMachines.Pleaseupload") });
        return;
      }

      // Generate image preview
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result); // Set the image preview
        setErrors({ ...errors, image: "" }); // Clear image errors
      };
      reader.readAsDataURL(file);
      setImageFile(file);
    }
  };

  const onlyPositiveNumberProps = {
    inputProps: {
      min: 1,
      inputMode: "decimal",
      pattern: "[0-9]*[.,]?[0-9]*", // optional dot/comma for decimal
      onWheel: (e) => e.target.blur(),
    },
    onWheel: (e) => e.target.blur(),
    onKeyDown: (e) => {
      const allowed = [
        "Backspace",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Delete",
        "Enter",
      ];
      const isNumber = /^\d$/.test(e.key);
      const isDot = e.key === ".";
      const alreadyHasDot = e.target.value.includes(".");

      if (!isNumber && !allowed.includes(e.key)) {
        if (isDot && !alreadyHasDot) {
          // Allow a single decimal point
          return;
        }
        e.preventDefault();
      }
    },
    onPaste: (e) => {
      const pasted = e.clipboardData.getData("text");
      if (!/^\d*\.?\d*$/.test(pasted)) {
        e.preventDefault(); // Block non-decimal or negative values
      }
    },
  };
  const isTablet = useMediaQuery("(max-width:900px)");

  // Code-based dropdown options: value is the numeric code sent to backend
  const paymentTypeOptions = [
    { value: "0", labelKey: "vendingMachines.paymentNayax" },
    { value: "1", labelKey: "vendingMachines.paymentCcvMApi" },
    { value: "2", labelKey: "vendingMachines.paymentCcvMdb" },
    { value: "3", labelKey: "vendingMachines.paymentDeltaMdb" },
  ];
  const aisleTypeOptions = [
    { value: "0", labelKey: "vendingMachines.aisleReturn" },
    { value: "2", labelKey: "vendingMachines.aisleFood2" },
    { value: "4", labelKey: "vendingMachines.aisleFood4" },
    { value: "6", labelKey: "vendingMachines.aislePizza6" },
    { value: "7", labelKey: "vendingMachines.aislePizza7" },
  ];

  const blueColor =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;

  return (
    <div className="main-container">
      {isLoading && <LoadingAnimation />}
      <Box
        className="AddMachineheaderSection"
        sx={{ display: "flex", justifyContent: "space-between" }}
      >
        {/* Back Button and Title */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            sx={{
              color: btnColor,
              backgroundColor: backgroundCard,
              padding: "0.6rem",
              borderRadius: "50%",
              marginRight: "1rem",
              "&:hover": { backgroundColor: backgroundCard },
            }}
            onClick={handleBackClick}
          >
            <WestIcon fontSize="small" />
          </IconButton>
          <h2 className="addmachine-text-heading">
            {machineData?.machineNumber
              ? t("vendingMachines.edit_machine")
              : t("vendingMachines.add_machine")}
          </h2>
        </Box>

        {/* Save Button */}
        <Box className="add_machine_save">
          <button
            type="button"
            onClick={handleSubmit}
            style={{ background: btnColor }}
            className="save-btn "
          >
            {t("membershipCards.save")}
          </button>
        </Box>
      </Box>

      <Box>
        <Box
          sx={{
            background: backgroundCard,
            p: { xs: 1, md: 2 },
            margin: "20px 0px",
            borderRadius: "12px",
          }}
        >
          {/* Error Message */}
          {errors.apiError && (
            <Typography
              sx={{
                color: errorTextColor,
                fontSize: "15px",
                marginBottom: "12px",
                textAlign: "center",
              }}
            >
              {errors.apiError}
            </Typography>
          )}

          {/* Section Title */}
          <h3 className="subheading-sec-card vending-form-heading">
            {t("vendingMachines.generalInformation")}
          </h3>
          {/* First Layout */}

          <div className="card-border-sec" style={{}}>
            <Grid container spacing={isTablet ? 0 : 10}>
              {/* Left Section */}
              <Grid item xs={12} md={4} className="general-hero-section ">
                {/* Machine No. */}

                <p className="label-text-sec">
                  {t("vendingMachines.MachineNo")}*
                </p>
                <Box
                  sx={{
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: inputBoxColor,
                    padding: "0.5rem 1rem",
                  }}
                >
                  <InputBase
                    name="machineNo"
                    value={formData.machineNo}
                    onChange={handleChange}
                    placeholder={t("vendingMachines.EnterSerialNo")}
                    fullWidth
                    disabled={mode1 === "update"}
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "& input": {
                        cursor: mode1 === "update" ? "not-allowed" : "text",
                      },
                      "&::placeholder": {
                        color: "#9CA3AF",
                      },
                    }}
                  />
                </Box>
                {errors.machineNo && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.machineNo}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={4} className="general-hero-section ">
                {/* Machine Type */}
                <p
                  className="label-text-sec"
                  sx={{
                    opacity:
                      mode1 === "update" ? "50% !important" : "100% !important",
                  }}
                >
                  {t("vendingMachines.MachineType")}*
                </p>

                <Box
                  sx={{
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: inputBoxColor,
                    padding: "0.5rem 1rem",
                    position: "relative",
                  }}
                >
                  <FormControl fullWidth>
                    <Select
                      className=" selectselectdropDown"
                      name="machineType"
                      value={formData.machineType}
                      onChange={handleChange}
                      disabled={mode1 === "update"}
                      IconComponent={dropDownIconComponent}
                      displayEmpty
                      sx={{
                        fontSize: "14px",
                        color: textColor,
                        backgroundColor: inputBoxColor,
                        boxShadow: "none",
                        padding: 0,

                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "& .MuiSelect-select": {
                          padding: "0.3rem 0.5rem",
                          height: "auto",
                          display: "flex",
                          alignItems: "center",
                        },
                      }}
                    >
                      {formData.machineType ? null : (
                        <MenuItem value="" disabled>
                          {t("vendingMachines.SelectMachine")}
                        </MenuItem>
                      )}
                      <MenuItem value="Hot Vending Machine">
                        {" "}
                        {t("vendingMachines.Hot")}
                      </MenuItem>
                      <MenuItem value="Normal Vending Machine">
                        {" "}
                        {t("vendingMachines.Normal")}
                      </MenuItem>
                      {/* <MenuItem value="Ice Vending Machine">
                        {" "}
                        {t("vendingMachines.Ice")}
                      </MenuItem> */}
                      <MenuItem value="Pizza Vending Machine">
                        {" "}
                        {t("vendingMachines.Pizza")}
                      </MenuItem>
                      <MenuItem value="Snack Vending Machine">
                        {" "}
                        {t("vendingMachines.Snack")}
                      </MenuItem>
                      <MenuItem value="Return Vending Machine">
                        {" "}
                        {t("vendingMachines.Return")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  {mode1 === "update" && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        cursor: "not-allowed",
                        zIndex: 2,
                      }}
                    />
                  )}
                </Box>
                {errors.machineType && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.machineType}
                  </Typography>
                )}
              </Grid>
              {/* Bowls Not Supported toggle (Hot / Normal machine types only) */}
              {supportsBowlsToggle(formData.machineType) && (
                <Grid item xs={12} md={4} className="general-hero-section">
                  {/* Invisible spacer label so the bar lines up with the
                      Machine Type dropdown box */}
                  <p
                    className="label-text-sec"
                    aria-hidden="true"
                    style={{ visibility: "hidden" }}
                  >
                    &nbsp;
                  </p>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor: inputBoxColor,
                      borderRadius: "8px",
                      padding: "0.5rem 1rem",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", color: textColor }}>
                      {t(
                        "vendingMachines.BowlsNotSupported",
                        "Bowls Not Supported",
                      )}
                    </Typography>
                    <Switch
                      name="bowlsNotSupported"
                      checked={Boolean(formData.bowlsNotSupported)}
                      onChange={(event) =>
                        setFormData((prev) => ({
                          ...prev,
                          bowlsNotSupported: event.target.checked,
                        }))
                      }
                      inputProps={{
                        "aria-label": t(
                          "vendingMachines.BowlsNotSupported",
                          "Bowls Not Supported",
                        ),
                      }}
                      sx={{
                        "& .MuiSwitch-switchBase": { color: "#FFFFFF" },
                        "& .MuiSwitch-track": {
                          backgroundColor: dropIcon,
                          opacity: 1,
                        },
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#FFFFFF",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { backgroundColor: btnColor, opacity: 1 },
                      }}
                    />
                  </Box>
                </Grid>
              )}
              {/* {formData.machineType === "Return Vending Machine" && (
                                <Grid item xs={12} md={4} className='general-hero-section'>

                                   
                                    <p className='label-text-sec'>{t('vendingMachines.AcceptedReturnItems')}*</p>
                                    <Box sx={{ border: 'none', borderRadius: '8px', }}>
                                        <FormControl fullWidth>
                                            <FormGroup
                                                row
                                                sx={{ gap: 2, }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={formData.returnItemType.includes('bowls')}
                                                            onChange={handleReturnItemToggle('bowls')}
                                                            disabled={mode1 === 'update'}
                                                            disableRipple
                                                            icon={<Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '1.5px solid', borderColor: dropIcon }} />}
                                                            checkedIcon={<BlueTickIcon color={blueColor} />}
                                                            sx={{
                                                                '&.Mui-checked': { color: btnColor },
                                                                '& .MuiSvgIcon-root': { fontSize: 20 },
                                                            }}
                                                        />
                                                    }
                                                    label={t('vendingMachines.bowls')}
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor: dropIcon,
                                                        borderRadius: '20px',
                                                        px: 2,
                                                        py: 0.25,
                                                        paddingLeft: '10px !important',
                                                        margin: 0,
                                                        '& .MuiFormControlLabel-label': {
                                                            fontSize: '14px',
                                                            color: textColor,
                                                        },
                                                    }}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={formData.returnItemType.includes('bottles')}
                                                            onChange={handleReturnItemToggle('bottles')}
                                                            disabled={mode1 === 'update'}
                                                            disableRipple

                                                            icon={<Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '1.5px solid', borderColor: dropIcon }} />}
                                                            checkedIcon={<BlueTickIcon color={blueColor} />}
                                                            sx={{
                                                                '&.Mui-checked': { color: btnColor },
                                                                '& .MuiSvgIcon-root': { fontSize: 20 },
                                                            }}
                                                        />
                                                    }
                                                    label={t('vendingMachines.bottles')}
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor: dropIcon,
                                                        borderRadius: '20px',
                                                        px: 2,
                                                        py: 0.5,
                                                        paddingLeft: '10px !important',

                                                        margin: 0,
                                                        '& .MuiFormControlLabel-label': {
                                                            fontSize: '14px',
                                                            color: textColor,
                                                        },
                                                    }}
                                                />
                                            </FormGroup>
                                        </FormControl>
                                    </Box>
                                    {errors.returnItemType && (
                                        <Typography sx={{ color: errorTextColor, fontSize: '12px', marginTop: '2px' }}>
                                            {errors.returnItemType}
                                        </Typography>
                                    )}

                                </Grid>
                            )} */}
            </Grid>
          </div>

          <Divider className="card-border-sec" />

          <div className="card-border-sec" style={{ marginTop: "20px" }}>
            <Grid container spacing={isTablet ? 0 : 10}>
              {/* Left Section */}
              <Grid
                item
                xs={12}
                md={4}
                id="vend-state1"
                className="stategrid5 "
              >
                {/* Location */}
                <p className="label-text-sec">
                  {" "}
                  {t("vendingMachines.Location")}*
                </p>
                <Box
                  sx={{
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: inputBoxColor,
                    padding: "0.5rem 1rem",
                  }}
                >
                  <InputBase
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder={t("vendingMachines.EnterLocation")}
                    fullWidth
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "&::placeholder": {
                        color: "#9CA3AF",
                      },
                    }}
                  />
                </Box>
                {errors.location && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.location}
                  </Typography>
                )}

                {/* Street Address */}
                <p className="label-text-sec">
                  {" "}
                  {t("vendingMachines.Street")}*
                </p>
                <Box
                  sx={{
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: inputBoxColor,
                    padding: "0.5rem 1rem",
                  }}
                >
                  <InputBase
                    name="streetAddress"
                    value={formData.streetAddress}
                    onChange={handleChange}
                    placeholder={t("vendingMachines.EnterStreetAddress")}
                    fullWidth
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "&::placeholder": {
                        color: "#9CA3AF",
                      },
                    }}
                  />
                </Box>
                {errors.streetAddress && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.streetAddress}
                  </Typography>
                )}

                {/* City */}
                <p className="label-text-sec"> {t("vendingMachines.City")}*</p>
                <Box
                  sx={{
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: inputBoxColor,
                    padding: "0.5rem 1rem",
                  }}
                >
                  <InputBase
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder={t("vendingMachines.EnterCity")}
                    fullWidth
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "&::placeholder": {
                        color: "#9CA3AF",
                      },
                    }}
                  />
                </Box>
                {errors.city && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.city}
                  </Typography>
                )}
              </Grid>

              {/* Middle Section */}
              <Grid item xs={12} md={4} id="vend-state2" className="stategrid">
                {/* State */}
                <p className="label-text-sec">{t("vendingMachines.State")}*</p>
                <Box
                  sx={{
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: inputBoxColor,
                    padding: "0.5rem 1rem",
                  }}
                >
                  <InputBase
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder={t("vendingMachines.EnterState")}
                    fullWidth
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "&::placeholder": {
                        color: "#9CA3AF",
                      },
                    }}
                  />
                </Box>
                {errors.state && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.state}
                  </Typography>
                )}

                {/* Zip */}
                <p className="label-text-sec">{t("vendingMachines.Zip")}*</p>
                <Box
                  sx={{
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: inputBoxColor,
                    padding: "0.5rem 1rem",
                    "& input[type=number]": {
                      MozAppearance: "textfield", // Firefox
                    },
                    "& input[type=number]::-webkit-outer-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  }}
                >
                  <InputBase
                    name="zip"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={formData.zip}
                    onChange={handleChange}
                    placeholder="000000"
                    fullWidth
                    {...onlyPositiveNumberProps}
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "&::placeholder": {
                        color: "#9CA3AF",
                      },
                    }}
                  />
                </Box>
                {errors.zip && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.zip}
                  </Typography>
                )}

                {/* Latitude & Longitude  */}
                <Box sx={{ display: "flex", gap: 2, marginBottom: "1rem" }}>
                  <Box sx={{ flex: 1 }}>
                    <p className="label-text-sec">
                      {t("vendingMachines.Latitude")}*
                    </p>
                    <Box
                      sx={{
                        border: "none",
                        borderRadius: "8px",
                        backgroundColor: inputBoxColor,
                        padding: "0.5rem 1rem",
                        "& input[type=number]": {
                          MozAppearance: "textfield", // Firefox
                        },
                        "& input[type=number]::-webkit-outer-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                        "& input[type=number]::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      }}
                    >
                      <InputBase
                        name="latitude"
                        type="number"
                        inputProps={{
                          min: -90,
                          max: 90,
                          step: "any",
                          onWheel: (e) => e.target.blur(),
                        }}
                        onWheel={(e) => e.target.blur()}
                        value={formData.latitude}
                        onChange={handleChange}
                        placeholder="00.00"
                        fullWidth
                        sx={{
                          fontSize: "14px",
                          color: textColor,
                          "&::placeholder": {
                            color: "#9CA3AF",
                          },
                        }}
                      />
                    </Box>
                    {errors.latitude && (
                      <Typography
                        sx={{
                          color: errorTextColor,
                          fontSize: "12px",
                          marginTop: "2px",
                        }}
                      >
                        {errors.latitude}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <p className="label-text-sec">
                      {" "}
                      {t("vendingMachines.Longitude")}*
                    </p>
                    <Box
                      sx={{
                        border: "none",
                        borderRadius: "8px",
                        backgroundColor: inputBoxColor,
                        padding: "0.5rem 1rem",
                        "& input[type=number]": {
                          MozAppearance: "textfield", // Firefox
                        },
                        "& input[type=number]::-webkit-outer-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                        "& input[type=number]::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      }}
                    >
                      <InputBase
                        name="longitude"
                        type="number"
                        inputProps={{
                          min: -180,
                          max: 180,
                          step: "any",
                          onWheel: (e) => e.target.blur(),
                        }}
                        onWheel={(e) => e.target.blur()}
                        value={formData.longitude}
                        onChange={handleChange}
                        placeholder="00.00"
                        fullWidth
                        sx={{
                          fontSize: "14px",
                          color: textColor,
                          "&::placeholder": {
                            color: "#9CA3AF",
                          },
                        }}
                      />
                    </Box>
                    {errors.longitude && (
                      <Typography
                        sx={{
                          color: errorTextColor,
                          fontSize: "12px",
                          marginTop: "2px",
                        }}
                      >
                        {errors.longitude}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Map */}
                {/* <p className='label-text-sec'> {t('vendingMachines.map')}</p>
                            <Box sx={{ border: 'none', borderRadius: '8px', backgroundColor: inputBoxColor, padding: '0.5rem 1rem' }}>
                                <InputBase
                                    name="map"
                                    value={formData.map}
                                    onChange={handleChange}
                                    placeholder={t('vendingMachines.EnterMapDetails')}
                                    fullWidth
                                    sx={{
                                        fontSize: '14px',
                                        color: textColor,
                                        '&::placeholder': {
                                            color: '#9CA3AF',
                                        },
                                    }}
                                />
                            </Box> */}
              </Grid>

              {/* Right Section */}
              <Grid item xs={12} md={4} id="vend-state3" className="stategrid2">
                {/* Image Upload */}
                <Box
                  sx={{
                    border: "1px dashed #D1D5DB",
                    borderRadius: "8px",
                    textAlign: "center",
                    cursor: "pointer",
                    position: "relative",
                    width: "100%",
                    aspectRatio: "4 / 3",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                  onClick={() =>
                    document.getElementById("upload-image").click()
                  }
                >
                  <input
                    type="file"
                    id="upload-image"
                    style={{ display: "none" }}
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                  ) : (
                    <div>
                      <AddPhotoAlternateIcon
                        sx={{
                          fontSize: "6rem",
                          color: "#C6C6C6",
                        }}
                      />
                      <Typography sx={{ color: "#4A4A4A" }}>
                        {t("vendingMachines.AddMachineImage")}
                      </Typography>
                    </div>
                  )}
                </Box>
                <Typography
                  className
                  sx={{
                    fontSize: "10px",
                    color: textColor,
                    textAlign: "center",
                    margin: "3px 0px",
                  }}
                >
                  {t("vendingMachines.uploadImageFormatHint")}
                </Typography>
              </Grid>
            </Grid>
          </div>
        </Box>

        <Box
          sx={{
            background: backgroundCard,
            p: 2,
            margin: "20px 0px",
            borderRadius: "12px",
          }}
        >
          {/* Section Title */}
          <h3
            className="subheading-sec-card vending-form-heading"
            style={{ color: textColor }}
          >
            {t("vendingMachines.machineConfiguration")}
          </h3>
          {/* Physical Dimensions Sub-header */}
          <Typography
            className="machine-sub-header card-border-sec"
            sx={{ color: subHeadingGrey }}
          >
            {t("vendingMachines.physicalDimensions")}
          </Typography>
          {/* Second Layout */}

          <div className="" style={{}}>
            <Grid container spacing={isTablet ? 0 : 10}>
              {/* Left Section */}
              <Grid item xs={12} md={4} id="vend-state4" className="stategrid5">
                {/* Length */}
                <p className="label-text-sec"> {t("vendingMachines.Length")}</p>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: inputBoxColor,
                    borderRadius: "8px",
                    padding: "0.5rem 1rem",
                    "& input[type=number]": {
                      MozAppearance: "textfield", // Firefox
                    },
                    "& input[type=number]::-webkit-outer-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  }}
                >
                  <InputBase
                    name="length"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={formData.machineConfig.length || ""}
                    onChange={handleChange}
                    placeholder="0.0"
                    fullWidth
                    {...onlyPositiveNumberProps}
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "&::placeholder": {
                        color: "#9CA3AF",
                      },
                    }}
                  />
                  <Typography sx={{ marginLeft: "5px", color: "#9CA3AF" }}>
                    mm
                  </Typography>
                </Box>
                {errors.length && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.length}
                  </Typography>
                )}

                {/* Height */}
                <p className="label-text-sec"> {t("vendingMachines.Height")}</p>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: inputBoxColor,
                    borderRadius: "8px",
                    padding: "0.5rem 1rem",
                    "& input[type=number]": {
                      MozAppearance: "textfield", // Firefox
                    },
                    "& input[type=number]::-webkit-outer-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  }}
                >
                  <InputBase
                    name="height"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={formData.machineConfig.height || ""}
                    onChange={handleChange}
                    placeholder="0.0"
                    {...onlyPositiveNumberProps}
                    fullWidth
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "&::placeholder": {
                        color: "#9CA3AF",
                      },
                    }}
                  />
                  <Typography sx={{ marginLeft: "5px", color: "#9CA3AF" }}>
                    mm
                  </Typography>
                </Box>
                {errors.height && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.height}
                  </Typography>
                )}

                {/* Width */}
                <p className="label-text-sec">{t("vendingMachines.Width")}</p>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: inputBoxColor,
                    borderRadius: "8px",
                    padding: "0.5rem 1rem",
                    "& input[type=number]": {
                      MozAppearance: "textfield", // Firefox
                    },
                    "& input[type=number]::-webkit-outer-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  }}
                >
                  <InputBase
                    name="width"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={formData.machineConfig.width || ""}
                    onChange={handleChange}
                    placeholder="0.0"
                    {...onlyPositiveNumberProps}
                    fullWidth
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "&::placeholder": {
                        color: "#9CA3AF",
                      },
                    }}
                  />
                  <Typography sx={{ marginLeft: "5px", color: "#9CA3AF" }}>
                    mm
                  </Typography>
                </Box>
                {errors.width && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.width}
                  </Typography>
                )}
              </Grid>

              {/* Middle Section */}
              {!isReturnMachine(formData.machineType) ? (
                <Grid
                  item
                  xs={12}
                  md={4}
                  id="vend-state5"
                  className="stategrid5"
                >
                  {/* Rows */}
                  <p className="label-text-sec">{t("vendingMachines.Rows")}*</p>
                  <Box
                    sx={{
                      backgroundColor: inputBoxColor,
                      borderRadius: "8px",
                      padding: "0.5rem 1rem",
                      "& input[type=number]": {
                        MozAppearance: "textfield", // Firefox
                      },
                      "& input[type=number]::-webkit-outer-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                      "& input[type=number]::-webkit-inner-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                    }}
                  >
                    <InputBase
                      name="rows"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={formData.machineConfig.rows || ""}
                      onChange={handleChange}
                      placeholder="00"
                      {...onlyPositiveNumberProps}
                      fullWidth
                      sx={{
                        fontSize: "14px",
                        color: textColor,
                        "&::placeholder": {
                          color: "#9CA3AF",
                        },
                      }}
                    />
                  </Box>
                  {errors.rows && (
                    <Typography
                      sx={{
                        color: errorTextColor,
                        fontSize: "12px",
                        marginTop: "2px",
                      }}
                    >
                      {errors.rows}
                    </Typography>
                  )}

                  {/* Columns */}
                  <p className="label-text-sec">
                    {t("vendingMachines.Columns")}*
                  </p>
                  <Box
                    sx={{
                      backgroundColor: inputBoxColor,
                      borderRadius: "8px",
                      padding: "0.5rem 1rem",
                      "& input[type=number]": {
                        MozAppearance: "textfield", // Firefox
                      },
                      "& input[type=number]::-webkit-outer-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                      "& input[type=number]::-webkit-inner-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                    }}
                  >
                    <InputBase
                      name="columns"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={formData.machineConfig.columns || ""}
                      onChange={handleChange}
                      placeholder="00"
                      {...onlyPositiveNumberProps}
                      fullWidth
                      sx={{
                        fontSize: "14px",
                        color: textColor,
                        "&::placeholder": {
                          color: "#9CA3AF",
                        },
                      }}
                    />
                  </Box>
                  {errors.columns && (
                    <Typography
                      sx={{
                        color: errorTextColor,
                        fontSize: "12px",
                        marginTop: "2px",
                      }}
                    >
                      {errors.columns}
                    </Typography>
                  )}

                  {/* Slots */}
                  {/* <p className='label-text-sec'>Slots</p>
                            <Box sx={{ backgroundColor: inputBoxColor, borderRadius: '8px', padding: '0.5rem 1rem' }}>
                                <InputBase
                                    name="slots"
                                    type="number"
                                    value={formData.machineConfig.slots || ''}
                                    onChange={handleChange}
                                    placeholder="00"
                                    fullWidth
                                    sx={{
                                        fontSize: '14px',
                                        color: textColor,
                                        '&::placeholder': {
                                            color: '#9CA3AF',
                                        },
                                    }}
                                />
                            </Box>
                            {errors.slots && (
                                <Typography sx={{ color: errorTextColor, fontSize: '12px', marginTop: '2px' }}>
                                    {errors.slots}
                                </Typography>
                            )} */}
                </Grid>
              ) : null}

              {/* Right Section */}
              <Grid item xs={12} md={4} id="vend-state6" className="stategrid5">
                {/* Weight */}
                <p className="label-text-sec">{t("vendingMachines.Weight")}</p>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: inputBoxColor,
                    borderRadius: "8px",
                    padding: "0.5rem 1rem",
                    "& input[type=number]": {
                      MozAppearance: "textfield", // Firefox
                    },
                    "& input[type=number]::-webkit-outer-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  }}
                >
                  <InputBase
                    name="weight"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={formData.machineConfig.weight || ""}
                    onChange={handleChange}
                    placeholder="0.0"
                    {...onlyPositiveNumberProps}
                    fullWidth
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "&::placeholder": {
                        color: "#9CA3AF",
                      },
                    }}
                  />
                  <Typography sx={{ marginLeft: "5px", color: "#9CA3AF" }}>
                    Kg
                  </Typography>
                </Box>
                {errors.weight && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.weight}
                  </Typography>
                )}
              </Grid>
            </Grid>
            {isReturnMachine(formData.machineType) ? (
              <>
                {/* Internal Layout & Capacity Sub-header */}
                <Divider className="card-border-sec" />

                <Typography
                  className="machine-sub-header "
                  id="sub-header-margin"
                  sx={{ color: subHeadingGrey }}
                >
                  {t("vendingMachines.internalLayoutCapacity")}
                </Typography>

                <Grid container spacing={isTablet ? 0 : 10}>
                  {/* Column 1: Bowl Cashback + Bowl Capacity */}
                  <Grid item xs={12} md={4} className="stategrid5">
                    {/* Bowl Cashback */}

                    <p className="label-text-sec">
                      {t("vendingMachines.bowlCashback")}*
                    </p>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: inputBoxColor,
                        borderRadius: "8px",
                        padding: "0.5rem 1rem",
                        "& input[type=number]": {
                          MozAppearance: "textfield",
                        },
                        "& input[type=number]::-webkit-outer-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                        "& input[type=number]::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      }}
                    >
                      <InputBase
                        name="bowlCashback"
                        type="number"
                        {...onlyPositiveNumberProps}
                        inputProps={{
                          ...onlyPositiveNumberProps.inputProps,
                          min: 0,
                          max: 100,
                        }}
                        placeholder="2"
                        value={formData.machineConfig.bowlCashback ?? ""}
                        onChange={handleChange}
                        fullWidth
                        sx={{ fontSize: "14px", color: textColor }}
                      />
                      <Typography sx={{ marginLeft: "5px", color: "#9CA3AF" }}>
                        €
                      </Typography>
                    </Box>
                    {errors.bowlCashback && (
                      <Typography
                        sx={{
                          color: errorTextColor,
                          fontSize: "12px",
                          marginTop: "2px",
                        }}
                      >
                        {errors.bowlCashback}
                      </Typography>
                    )}

                    <p className="label-text-sec">
                      {t("vendingMachines.bottleCashback")}*
                    </p>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: inputBoxColor,
                        borderRadius: "8px",
                        padding: "0.5rem 1rem",
                        "& input[type=number]": {
                          MozAppearance: "textfield",
                        },
                        "& input[type=number]::-webkit-outer-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                        "& input[type=number]::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      }}
                    >
                      <InputBase
                        name="bottleCashback"
                        type="number"
                        {...onlyPositiveNumberProps}
                        inputProps={{
                          ...onlyPositiveNumberProps.inputProps,
                          min: 0,
                          max: 100,
                        }}
                        placeholder="0.25"
                        value={formData.machineConfig.bottleCashback ?? ""}
                        onChange={handleChange}
                        fullWidth
                        sx={{ fontSize: "14px", color: textColor }}
                      />
                      <Typography sx={{ marginLeft: "5px", color: "#9CA3AF" }}>
                        €
                      </Typography>
                    </Box>
                    {errors.bottleCashback && (
                      <Typography
                        sx={{
                          color: errorTextColor,
                          fontSize: "12px",
                          marginTop: "2px",
                        }}
                      >
                        {errors.bottleCashback}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </>
            ) : null}

            {/* {formData.machineType?.toLowerCase().includes('return') ? (
                            <>
                                <Divider className='card-border-sec' />

                                <Typography className='machine-sub-header ' id="sub-header-margin" sx={{ color: subHeadingGrey, }}>
                                    {t('vendingMachines.internalLayoutCapacity')}
                                </Typography>

                                <Grid container spacing={isTablet ? 0 : 10}>
                                    Column 1: Bowl Cashback + Bowl Capacity
                                    <Grid item xs={12} md={4} className='stategrid5'>
                                        Bowl Cashback
                                        {formData.returnItemType.includes('bowls') && (
                                            <>
                                                <p className='label-text-sec'>{t('vendingMachines.bowlCashback')}*</p>
                                                <Box sx={{
                                                    display: 'flex', alignItems: 'center', backgroundColor: inputBoxColor, borderRadius: '8px', padding: '0.5rem 1rem', '& input[type=number]': {
                                                        MozAppearance: 'textfield',
                                                    },
                                                    '& input[type=number]::-webkit-outer-spin-button': {
                                                        WebkitAppearance: 'none',
                                                        margin: 0,
                                                    },
                                                    '& input[type=number]::-webkit-inner-spin-button': {
                                                        WebkitAppearance: 'none',
                                                        margin: 0,
                                                    },
                                                }}>
                                                    <InputBase
                                                        name="bowlCashback"
                                                        type="number"
                                                        value={2}
                                                        readOnly
                                                        fullWidth
                                                        sx={{
                                                            fontSize: '14px',
                                                            color: textColor,
                                                        }}
                                                    />
                                                    <Typography sx={{ marginLeft: '5px', color: '#9CA3AF' }}>€</Typography>
                                                </Box>
                                            </>
                                        )}

                                        Bowl Capacity
                                        {formData.returnItemType.includes('bowls') && (
                                            <>
                                                <p className='label-text-sec'>{t('vendingMachines.bowlCapacity')}</p>
                                                <Box sx={{
                                                    display: 'flex', alignItems: 'center', backgroundColor: inputBoxColor, borderRadius: '8px', padding: '0.5rem 1rem',
                                                    '& input[type=number]': {
                                                        MozAppearance: 'textfield',
                                                    },
                                                    '& input[type=number]::-webkit-outer-spin-button': {
                                                        WebkitAppearance: 'none',
                                                        margin: 0,
                                                    },
                                                    '& input[type=number]::-webkit-inner-spin-button': {
                                                        WebkitAppearance: 'none',
                                                        margin: 0,
                                                    },
                                                }}>
                                                    <InputBase
                                                        name="bowlCapacity"
                                                        type="number"
                                                        inputProps={{ min: 0 }}
                                                        value={formData.machineConfig.bowlCapacity || ''}
                                                        onChange={handleChange}
                                                        placeholder="0"
                                                        {...onlyPositiveNumberProps}
                                                        fullWidth
                                                        sx={{
                                                            fontSize: '14px',
                                                            color: textColor,
                                                            '&::placeholder': {
                                                                color: '#9CA3AF',
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            </>
                                        )}
                                    </Grid>

                                    Column 2: Bottle Capacity + Bottle Cashback
                                    <Grid item xs={12} md={4} className='stategrid5'>


                                        Bottle Cashback
                                        {formData.returnItemType.includes('bottles') && (
                                            <>
                                                <p className='label-text-sec'>{t('vendingMachines.bottleCashback')}*</p>
                                                <Box sx={{
                                                    display: 'flex', alignItems: 'center', backgroundColor: inputBoxColor, borderRadius: '8px', padding: '0.5rem 1rem', '& input[type=number]': {
                                                        MozAppearance: 'textfield',
                                                    },
                                                    '& input[type=number]::-webkit-outer-spin-button': {
                                                        WebkitAppearance: 'none',
                                                        margin: 0,
                                                    },
                                                    '& input[type=number]::-webkit-inner-spin-button': {
                                                        WebkitAppearance: 'none',
                                                        margin: 0,
                                                    },
                                                }}>
                                                    <InputBase
                                                        name="bottleCashback"
                                                        type="number"
                                                        value={0.25}
                                                        readOnly
                                                        fullWidth
                                                        sx={{
                                                            fontSize: '14px',
                                                            color: textColor,
                                                        }}
                                                    />
                                                    <Typography sx={{ marginLeft: '5px', color: '#9CA3AF' }}>€</Typography>
                                                </Box>
                                            </>
                                        )}



                                        Bottle Capacity
                                        {formData.returnItemType.includes('bottles') && (
                                            <>
                                                <p className='label-text-sec'>{t('vendingMachines.bottleCapacity')}</p>
                                                <Box sx={{
                                                    display: 'flex', alignItems: 'center', backgroundColor: inputBoxColor, borderRadius: '8px', padding: '0.5rem 1rem', '& input[type=number]': {
                                                        MozAppearance: 'textfield',
                                                    },
                                                    '& input[type=number]::-webkit-outer-spin-button': {
                                                        WebkitAppearance: 'none',
                                                        margin: 0,
                                                    },
                                                    '& input[type=number]::-webkit-inner-spin-button': {
                                                        WebkitAppearance: 'none',
                                                        margin: 0,
                                                    },
                                                }}>
                                                    <InputBase
                                                        name="bottleCapacity"
                                                        type="number"
                                                        inputProps={{ min: 0 }}
                                                        value={formData.machineConfig.bottleCapacity || ''}
                                                        onChange={handleChange}
                                                        placeholder="0"
                                                        {...onlyPositiveNumberProps}
                                                        fullWidth
                                                        sx={{
                                                            fontSize: '14px',
                                                            color: textColor,
                                                            '&::placeholder': {
                                                                color: '#9CA3AF',
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                            </>
                        ) : null}  */}
          </div>
        </Box>
        {/* Android App Information */}
        <Box
          sx={{
            background: backgroundCard,
            p: 2,
            margin: "20px 0px",
            borderRadius: "12px",
          }}
        >
          {/* Section Title */}
          <h3 className="subheading-sec-card vending-form-heading" style={{}}>
            {t("vendingMachines.androidAppInformation")}
          </h3>
          {/* Android Layout */}
          <div className="card-border-sec" style={{ borderRadius: "8px" }}>
            <Grid container spacing={isTablet ? 0 : 10}>
              {/* App Version */}
              <Grid item xs={12} md={4} className="stategrid5">
                <p className="label-text-sec">
                  {t("vendingMachines.androidAppVersion")}
                </p>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: inputBoxColor,
                    borderRadius: "8px",
                    padding: "0.5rem 1rem",
                  }}
                >
                  <InputBase
                    name="version"
                    type="text"
                    value={formData.androidAppInfo.version}
                    onChange={handleChange}
                    placeholder="1.0.1"
                    fullWidth
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "&::placeholder": { color: "#9CA3AF" },
                    }}
                  />
                </Box>
                {/* Last Updated */}
                <p className="label-text-sec">
                  {t("vendingMachines.androidLastUpdated")}
                </p>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: inputBoxColor,
                    borderRadius: "8px",
                    padding: "0.5rem 1rem",
                  }}
                >
                  <InputBase
                    name="lastUpdatedVersion"
                    type="text"
                    value={formData.androidAppInfo.lastUpdatedVersion}
                    onChange={handleChange}
                    placeholder="DD MMM YYYY"
                    fullWidth
                    disabled
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "&::placeholder": { color: "#9CA3AF" },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4} className="stategrid">
                {/* Payment type */}
                <p className="label-text-sec">
                  {t("vendingMachines.paymentType")}
                </p>
                <Box
                  sx={{
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: inputBoxColor,
                  }}
                >
                  <FormControl fullWidth>
                    <Select
                      name="paymentType"
                      value={formData.androidAppInfo.paymentType}
                      onChange={handleChange}
                      displayEmpty
                      IconComponent={dropDownIconComponent}
                      renderValue={(v) => {
                        if (!v) return t("vendingMachines.Select");
                        const opt = paymentTypeOptions.find(
                          (o) => o.value === v,
                        );
                        return opt ? t(opt.labelKey) : v;
                      }}
                      sx={{
                        fontSize: "14px",
                        color: textColor,
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        padding: "0.3rem 0.5rem",

                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "& .MuiSelect-select": {
                          padding: "0.5rem 0.5rem",
                          height: "auto",
                          display: "flex",
                          alignItems: "center",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        {t("vendingMachines.Select")}
                      </MenuItem>
                      {paymentTypeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Aisle type */}
                {/* Hide Aisle Type for Recycle/Return Machines */}
                {!isReturnMachine(formData.machineType) && (
                  <>
                    <p className="label-text-sec">
                      {t("vendingMachines.aisleType")}
                    </p>

                    <Box
                      sx={{
                        border: "none",
                        borderRadius: "8px",
                        backgroundColor: inputBoxColor,
                      }}
                    >
                      <FormControl fullWidth>
                        <Select
                          name="aisleType"
                          value={formData.androidAppInfo.aisleType}
                          onChange={handleChange}
                          displayEmpty
                          IconComponent={dropDownIconComponent}
                          renderValue={(v) => {
                            if (!v) return t("vendingMachines.Select");
                            const opt = aisleTypeOptions.find(
                              (o) => o.value === v,
                            );
                            return opt ? t(opt.labelKey) : v;
                          }}
                          sx={{
                            fontSize: "14px",
                            color: textColor,
                            backgroundColor: "transparent",
                            boxShadow: "none",
                            padding: "0.3rem 0.5rem",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                            "& .MuiSelect-select": {
                              padding: "0.5rem 0.5rem",
                              height: "auto",
                              display: "flex",
                              alignItems: "center",
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            {t("vendingMachines.Select")}
                          </MenuItem>

                          {aisleTypeOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {t(opt.labelKey)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </>
                )}
              </Grid>

              {/* App Notes */}
              <Grid item xs={12} className="stategrid appnote">
                <p className="label-text-sec">
                  {t("vendingMachines.androidAppNotes")}
                </p>
                <Box
                  sx={{
                    backgroundColor: inputBoxColor,
                    borderRadius: "8px",
                    padding: "0.5rem 1rem",
                  }}
                >
                  <InputBase
                    name="versionNotes"
                    type="text"
                    multiline
                    rows={3}
                    value={formData.androidAppInfo.versionNotes}
                    onChange={handleChange}
                    placeholder={t("vendingMachines.enterAppNotes")}
                    fullWidth
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "&::placeholder": { color: "#9CA3AF" },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </div>
        </Box>

        <Box
          sx={{
            background: backgroundCard,
            p: 2,
            margin: "20px 0px",
            borderRadius: "12px",
          }}
        >
          {/* Section Title */}
          <h3 className="subheading-sec-card vending-form-heading" style={{}}>
            {t("vendingMachines.connectivityPowerManagement")}
          </h3>
          {/* Third Layout */}
          <div className="card-border-sec" style={{ borderRadius: "8px" }}>
            <Grid container spacing={isTablet ? 0 : 10}>
              {/* Left Section */}
              <Grid item xs={12} md={4} id="vend-state7" className="stategrid5">
                {/* Network Connection Type */}
                <p className="label-text-sec">
                  {" "}
                  {t("vendingMachines.NetworkConnection")}*{" "}
                </p>
                <Box
                  sx={{
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: inputBoxColor,
                    padding: "0.5rem 1rem",
                  }}
                >
                  <FormControl fullWidth>
                    <Select
                      name="networkConnectionType"
                      value={
                        formData.connectivityAndPower.networkConnectionType
                      }
                      onChange={handleChange}
                      displayEmpty
                      IconComponent={dropDownIconComponent}
                      sx={{
                        fontSize: "14px",
                        color: textColor,
                        backgroundColor: inputBoxColor,
                        boxShadow: "none",
                        padding: 0,
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "& .MuiSelect-select": {
                          padding: "0.3rem 0.5rem",
                          height: "auto",
                          display: "flex",
                          alignItems: "center",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        {t("vendingMachines.SelectConnection")}
                      </MenuItem>
                      <MenuItem value="WiFi">
                        {" "}
                        {t("vendingMachines.WiFi")}{" "}
                      </MenuItem>
                      <MenuItem value="Ethernet">
                        {t("vendingMachines.Ethernet")}
                      </MenuItem>
                      <MenuItem value="4G">
                        {" "}
                        {t("vendingMachines.4G")}{" "}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {errors.networkConnectionType && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.networkConnectionType}
                  </Typography>
                )}

                {/* MAC Address */}
                {/* <p className='label-text-sec'>  {t('vendingMachines.MAC')}</p>
                                <Box sx={{ border: 'none', borderRadius: '8px', backgroundColor: inputBoxColor, padding: '0.5rem 1rem' }}>
                                    <InputBase
                                        name="macAddress"
                                        value={formData.connectivityAndPower.macAddress}
                                        onChange={handleChange}
                                        placeholder={t('vendingMachines.EnterMACAddress')}
                                        fullWidth
                                        sx={{
                                            fontSize: '14px',
                                            color: textColor,
                                            '&::placeholder': { color: '#9CA3AF' },
                                        }}
                                    />
                                </Box>
                                {errors.macAddress && (
                                    <Typography sx={{ color: errorTextColor, fontSize: '12px', marginTop: '2px' }}>
                                        {errors.macAddress}
                                    </Typography>
                                )} */}
              </Grid>

              {/* Middle Section */}
              <Grid item xs={12} md={4} id="vend-state8" className="stategrid">
                {/* Power Source */}
                {/* <p className='label-text-sec'>  {t('vendingMachines.PowerSource')}</p>
                                <Box sx={{ border: 'none', borderRadius: '8px', backgroundColor: inputBoxColor, padding: '0.5rem 1rem' }}>
                                    <FormControl fullWidth>
                                        <Select
                                            name="powerSource"
                                            className='select'
                                            value={formData.connectivityAndPower.powerSource}
                                            onChange={handleChange}
                                            displayEmpty
                                            IconComponent={dropDownIconComponent}
                                            sx={{
                                                fontSize: '14px',
                                                color: textColor,
                                                backgroundColor: inputBoxColor,
                                                boxShadow: 'none',
                                                padding: 0,
                                                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                                '& .MuiSelect-select': { padding: '0.35rem 0.5rem', height: 'auto', display: 'flex', alignItems: 'center' },
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                {t('vendingMachines.SelectPower')}
                                            </MenuItem>
                                            <MenuItem value="Plug-in Electric">{t('vendingMachines.Plug')}</MenuItem>
                                            <MenuItem value="Battery">{t('vendingMachines.Battery')}</MenuItem>
                                            <MenuItem value="Solar Powered"> {t('vendingMachines.Solar')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                {errors.powerSource && (
                                    <Typography sx={{ color: errorTextColor, fontSize: '12px', marginTop: '2px' }}>
                                        {errors.powerSource}
                                    </Typography>
                                )} */}

                {/* Power Consumption */}
                <p className="label-text-sec">
                  {t("vendingMachines.PowerConsumption")}
                </p>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: inputBoxColor,
                    borderRadius: "8px",
                    padding: "0.5rem 1rem",
                    "& input[type=number]": {
                      MozAppearance: "textfield", // Firefox
                    },
                    "& input[type=number]::-webkit-outer-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  }}
                >
                  <InputBase
                    name="powerConsumption"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={formData.connectivityAndPower.powerConsumption}
                    onChange={handleChange}
                    placeholder="0W"
                    {...onlyPositiveNumberProps}
                    fullWidth
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "&::placeholder": { color: "#9CA3AF" },
                    }}
                  />
                  <Typography sx={{ marginLeft: "5px", color: "#9CA3AF" }}>
                    W
                  </Typography>
                </Box>
                {errors.powerConsumption && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.powerConsumption}
                  </Typography>
                )}
              </Grid>

              {/* Right Section */}
              <Grid
                item
                xs={12}
                md={4}
                id="vend-state9"
                className="stategrid"
              ></Grid>
            </Grid>
          </div>
        </Box>

        <Box
          sx={{
            background: backgroundCard,
            p: 2,
            margin: "20px 0px",
            borderRadius: "12px",
          }}
        >
          {/* Section Title */}
          <h3 className="subheading-sec-card vending-form-heading" style={{}}>
            {t("vendingMachines.activationWarranty")}
          </h3>
          {/* fourth Layout */}
          <div className="card-border-sec" style={{}}>
            <Grid container spacing={isTablet ? 0 : 10}>
              {/* Left Section */}
              <Grid item xs={12} md={4} className="stategrid5">
                {/* Activated Date */}
                <p className="label-text-sec">
                  {t("vendingMachines.ActivatedDate")}{" "}
                </p>
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: inputBoxColor,
                    borderRadius: "8px",
                    padding: "0.5rem 1rem",
                  }}
                >
                  <InputBase
                    type="text"
                    readOnly
                    value={
                      formData?.connectivityAndPower?.activationDate
                        ? formatDate(
                            formData.connectivityAndPower.activationDate,
                          )
                        : ""
                    }
                    placeholder="Select Date"
                    fullWidth
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      cursor: "pointer",
                      "&::placeholder": { color: "#9CA3AF" },
                    }}
                    onClick={() => dateInputRef.current?.showPicker()}
                  />
                  <input
                    type="date"
                    ref={dateInputRef}
                    value={formData?.connectivityAndPower?.activationDate}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        connectivityAndPower: {
                          ...formData.connectivityAndPower,
                          activationDate: event.target.value,
                        },
                      })
                    }
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                    }}
                  />
                  <CalendarToday
                    sx={{
                      fontSize: "14px",
                      position: "absolute",
                      right: 17,
                      color: dropIcon,
                      cursor: "pointer",
                    }}
                    onClick={() => dateInputRef.current?.showPicker()}
                  />
                </Box>
                {errors.activationDate && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.activationDate}
                  </Typography>
                )}
              </Grid>

              {/* Middle Section */}
              <Grid item xs={12} md={4} id="vend-state11" className="stategrid">
                {/* Warranty Period */}
                <p className="label-text-sec">
                  {" "}
                  {t("vendingMachines.Warranty")}
                </p>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: inputBoxColor,
                    borderRadius: "8px",
                    padding: "0.5rem 1rem",
                    "& input[type=number]": {
                      MozAppearance: "textfield", // Firefox
                    },
                    "& input[type=number]::-webkit-outer-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  }}
                >
                  <InputBase
                    name="warrantyPeriod"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={formData.connectivityAndPower.warrantyPeriod}
                    onChange={(event) =>
                      setFormData({
                        ...formData,
                        connectivityAndPower: {
                          ...formData.connectivityAndPower,
                          warrantyPeriod: event.target.value,
                        },
                      })
                    }
                    placeholder="00"
                    fullWidth
                    {...onlyPositiveNumberProps}
                    sx={{
                      fontSize: "14px",
                      color: textColor,
                      "&::placeholder": { color: "#9CA3AF" },
                    }}
                  />
                  <Typography sx={{ marginLeft: "5px", color: "#9CA3AF" }}>
                    {t("dashboard.year")}
                  </Typography>
                </Box>
                {errors.warrantyPeriod && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {errors.warrantyPeriod}
                  </Typography>
                )}
              </Grid>

              {/* Right Section */}
              <Grid
                item
                xs={12}
                md={4}
                id="vend-state12"
                className="stategrid"
              ></Grid>
            </Grid>
          </div>
        </Box>

        {/* Screen Information Section */}
        {/* fifth Layout */}
        <Box
          sx={{
            background: backgroundCard,
            p: 2,
            margin: "20px 0px",
            borderRadius: "12px",
          }}
        >
          <h3
            className="subheading-sec-card vending-form-heading"
            style={{ margin: "0px" }}
          >
            {t("vendingMachines.screenInformation")}
          </h3>
          <div style={{}}>
            <ScreenInformationSection
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              inputBoxColor={inputBoxColor}
              textColor={textColor}
              errorColor={errorTextColor}
              subHeadingGrey={subHeadingGrey}
              dropCircle={dropCircle}
              dropIcon={dropIcon}
            />
          </div>
        </Box>
      </Box>

      {/* Render the Modal */}
      <ConfirmationModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSave={handleSaveChanges}
        onDiscard={handleDiscardChanges}
      />

      {/* Use Notification Component */}
      <SuccessNotification
        open={successNotification}
        message={
          mode1 === "add"
            ? t("vendingMachines.successMessage")
            : t("vendingMachines.successMessage1")
        }
        severity="success"
        onClose={() => setSuccessNotification(false)}
      />
    </div>
  );
}

export default VendingMachineModal;
